import type { SmsFeatureIcon } from "./smsModuleIcons";

export interface ModuleFeature {
  title: string;
  icon: SmsFeatureIcon;
  description?: string;
  items?: string[];
  subsections?: { title: string; items: string[] }[];
}

export interface SmsModule {
  id: string;
  title: string;
  description: string;
  features: ModuleFeature[];
}

export const smsModules: SmsModule[] = [
  {
    id: "student",
    title: "Student Module",
    description:
      "Designed to solve the communication gap between students and school management — helping both move forward without unnecessary delays.",
    features: [
      {
        title: "Login Access",
        icon: "login",
        description:
          "Separate user ID and password for every user. Initial dummy login details are provided; users can change their password while the login ID remains the same.",
      },
      {
        title: "Personal Details",
        icon: "personal-details",
        items: ["Full Name", "Father's Name", "Mother's Name", "Contact Details", "Blood Group"],
      },
      {
        title: "Student Details",
        icon: "student-details",
        description:
          "Complete student details displayed inclusive of personal and academic information. Students can cross-check and update their details via the dashboard.",
      },
      {
        title: "Academic Details",
        icon: "academic-details",
        items: ["Standard", "Subjects", "Class Teacher", "Co-curricular Activities", "Transport (if used)"],
      },
      {
        title: "Syllabus",
        icon: "syllabus",
        items: [
          "Subjects",
          "Allotting portions according to the term",
          "Details of teaching staffs according to the subjects",
          "Other Courses",
        ],
      },
      {
        title: "Time Table",
        icon: "timetable",
        description:
          "Regular class timetable updates with notification reminders whenever the schedule changes.",
      },
      {
        title: "Calendar",
        icon: "calendar",
        description:
          "Academic calendar showing term days, holidays, and continuously updated attendance information.",
      },
      {
        title: "Examination",
        icon: "examination",
        description:
          "Regular updates on tests and examinations with reminder notifications. Results can be viewed here.",
      },
      {
        title: "Report Card",
        icon: "report-card",
        description:
          "View academic progress and performance using a unique ID. Progress reports displayed after every term exam.",
      },
      {
        title: "Bus",
        icon: "bus",
        description:
          "Transportation details where students can opt for the facility. After submitting residential area details, available bus facilities are shared.",
      },
      {
        title: "Projects",
        icon: "projects",
        description:
          "Extra projects to enhance skills with full details provided by respective teachers.",
      },
      {
        title: "Interaction Point",
        icon: "interaction-point",
        description: "Share queries or information beyond basic details through this dedicated channel.",
      },
      {
        title: "Sports And Events",
        icon: "sports-events",
        description: "Informational notifications based on participation in sports and other events.",
      },
      {
        title: "Assignments",
        icon: "assignments",
        description: "Daily assignments for each subject with reminder alerts.",
      },
      {
        title: "Extra/Special Class",
        icon: "extra-class",
        description: "Reminders and updates for extra or special classes to help students prepare accordingly.",
      },
      {
        title: "Seminars",
        icon: "seminars",
        description:
          "Inter and intra school seminar details per the academic calendar. Students can register for relevant subjects with staff guidance.",
      },
      {
        title: "Library Corner",
        icon: "library",
        description:
          "Customized book details by subject and syllabus. Take, return, and renew books using Student ID and library account number.",
      },
      {
        title: "Board Exam Preparation",
        icon: "board-exam",
        description: "End-to-end board exam prep — syllabus, model papers, test papers, and class timings.",
      },
      {
        title: "Competitive Exam",
        icon: "competitive-exam",
        description:
          "Coaching details for various competitive exams with guidance from respective faculties.",
      },
    ],
  },
  {
    id: "parent",
    title: "Parent Module",
    description:
      "Creates proper communication between parents and school management for a hassle-free process with high benefit for all stakeholders.",
    features: [
      {
        title: "Login Access",
        icon: "login",
        description:
          "Parents register and receive temporary login credentials, then create authenticated login details shared with school management.",
      },
      {
        title: "Student Activities",
        icon: "student-activities",
        description:
          "All student activity information in one place. Parents track their children anywhere, anytime using the student ID with immediate update reminders.",
      },
      {
        title: "Academic Info",
        icon: "academic-info",
        items: [
          "Subjects",
          "Staff details",
          "Time Table",
          "Test and Exam dates",
          "Result Info",
          "Extra/Special class (if any)",
        ],
      },
      {
        title: "Transport",
        icon: "transport",
        items: ["Pick up and drop point", "Transportation charges", "Bus details (specific ID for bus)", "Timings"],
      },
      {
        title: "Attendance Info",
        icon: "attendance",
        items: ["Yearly", "Half-yearly", "Quarterly", "Monthly"],
        description: "Regular monthly attendance details sent to parents.",
      },
      {
        title: "Sports And Events Info",
        icon: "sports-events",
        description: "Updates on sports or events including notifications to parents of participating children.",
      },
      {
        title: "Fee Payment",
        icon: "fee-payment",
        description:
          "End-to-end fee payment details with term and payment options for flexible, hassle-free payments.",
        items: ["Credit/Debit card", "Net-banking", "RTGS", "Mobile payment"],
      },
      {
        title: "Library",
        icon: "library",
        description:
          "Books taken and used by the student from the school library, with reminder notifications sent to parents.",
      },
      {
        title: "Disciplinary Info",
        icon: "disciplinary",
        description:
          "In-disciplinary activities like rule violations or misbehavior updated immediately with parent reminders.",
      },
      {
        title: "Transfer Certificate",
        icon: "transfer-certificate",
        description: "All relevant transfer certificate information, customized for join or discontinue at any point.",
      },
      {
        title: "Transfer Message Center",
        icon: "message-center",
        description:
          "Interact with teaching staffs and school management to clarify queries about the child's academic activities.",
      },
    ],
  },
  {
    id: "teacher",
    title: "Teacher Module",
    description:
      "Exclusively designed for all teaching staff — physical trainers, music teachers, and other professionals — to carry out day-to-day work without interruption.",
    features: [
      {
        title: "Login Access",
        icon: "login",
        description:
          "Each staff receives a separate user ID to generate their own login. Full access granted after institution verification.",
      },
      {
        title: "Subjects Details",
        icon: "subjects",
        description: "Subjects handled by each teaching staff member displayed for clarity on classes to be taken.",
      },
      {
        title: "Library",
        icon: "library",
        description: "Separate library section with automated reminders for books used according to requirement.",
      },
      {
        title: "Syllabus",
        icon: "syllabus",
        description:
          "Complete academic year syllabus — essential for teachers handling multiple classes or batches.",
      },
      {
        title: "Message Center",
        icon: "message-center",
        description: "Open-end section to register queries or report issues faced at any point in time.",
      },
      {
        title: "Exam Cell",
        icon: "exam-cell",
        description:
          "Direct access to exam schedules, test updates, assignments, and project works. Teachers can edit syllabus included by the administrator if necessary.",
      },
      {
        title: "Transport",
        icon: "transport",
        description: "Optional section with bus details, timing, and pick-up point based on transportation preference.",
        items: ["Bus details", "Timing", "Pick up Point"],
      },
      {
        title: "Staff Details",
        icon: "staff-details",
        description: "Add, edit, and delete personal information and view details of other institute staff.",
        items: ["Name", "Personal Address", "Educational Qualification", "Subjects handled"],
      },
      {
        title: "Student Info",
        icon: "student-info",
        description: "Complete information about students handled by the teaching staff.",
        items: ["Student Name", "Class", "Subject", "Student ID", "Performance", "Remarks"],
      },
    ],
  },
  {
    id: "accountant",
    title: "Accountant Module",
    description:
      "Digitized account handling that reduces workload and cuts down errors — the right time to change your accountancy face.",
    features: [
      {
        title: "Personal Details",
        icon: "accountant-accounts",
        subsections: [
          {
            title: "Fee Payment Accounts",
            items: [
              "Payment history for every student",
              "Invoice generation",
              "Reminder alerts to parents regarding fee payment",
              "Fee details information to admin",
            ],
          },
          {
            title: "Salary Accounts",
            items: ["Salary paid to individual staffs", "Increment details", "Exam based conveyances"],
          },
          {
            title: "Furniture & Expenditures",
            items: [
              "Track available furniture and classroom accessories",
              "Expense over furniture and class accessories",
              "Estimate on future demand",
            ],
          },
          {
            title: "Auditing Reports",
            items: ["Clear details regarding existing resources", "Demand over resources in future"],
          },
        ],
      },
    ],
  },
  {
    id: "librarian",
    title: "Librarian Module",
    description:
      "Highly customized module for error-free library management through authenticated login access.",
    features: [
      {
        title: "Library Operations",
        icon: "library-operations",
        description: "Perform routine tasks smoothly with full software support.",
        items: [
          "Available quantity tracking",
          "Newly purchased books",
          "Sorting books under right section",
          "Matching books taken with respective student/teacher ID",
          "Raising complaint to admin regarding stolen books",
          "Updating management regarding books requirement",
          "Updating the price of each book frequently according to edition",
          "Conducting end-to-end auditing",
        ],
      },
    ],
  },
  {
    id: "admin",
    title: "Admin Module",
    description:
      "Central control hub for the entire school management system — students, staff, fees, inventory, and more.",
    features: [
      {
        title: "Students Hub",
        icon: "students-hub",
        description: "Complete information on every student, updatable from anywhere at any time.",
        subsections: [
          {
            title: "Basic Detail",
            items: ["Name", "Class", "Section", "Parents name", "Address", "Blood group", "Bus group (if used)"],
          },
          {
            title: "Admission",
            items: [
              "Advertisements done for admission",
              "Expenditure for admission",
              "Total number of admission done",
              "Admission under every class",
            ],
          },
          {
            title: "Arrangements",
            items: ["Class room arrangements", "Sports and event arrangements", "Tour arrangements", "Seminar arrangements"],
          },
          {
            title: "Fee Payment",
            items: [
              "Basic fee, Tuition fee, Book fee",
              "Extra fees: Transportation, Sports, Co-curricular activities",
              "Payment modes: Card, Net banking, Mobile payment, RTGS, Cash",
              "Payment time: Annual, Half-yearly, Quarterly, Monthly",
            ],
          },
          {
            title: "Attendance",
            items: ["Master attendance book maintained and displayed to students, teachers, and parents"],
          },
          {
            title: "Projects And Seminars",
            items: ["Academic projects and seminars displayed throughout the academic year"],
          },
          {
            title: "Academic — Syllabus",
            items: ["Subjects for each class", "Allocation of exam dates", "Segregating portion according to terms"],
          },
        ],
      },
      {
        title: "Staff Hub",
        icon: "staff-hub",
        description:
          "Complete information on teaching and non-teaching staff with class allocation, subjects handled, and responsibilities. Staff can access student details, syllabus, library info, exam cell, and notice board.",
      },
      {
        title: "Disciplinary Hub",
        icon: "disciplinary-hub",
        description:
          "Separate track over student disciplinary activities. Staff and students can upload messages to admin regarding in-disciplinary activities on campus.",
      },
      {
        title: "Hostel",
        icon: "hostel",
        description: "Complete accommodation details.",
        items: [
          "Number of rooms available",
          "Furniture present in the room",
          "Current student strength in hostel",
          "Pantry service details",
          "Laundry service details",
          "Hostel fee details",
        ],
      },
      {
        title: "Software And Audit Reports",
        icon: "software-audit",
        description:
          "Details on software used in the institution. Admin can implement, utilize, and update required software and manage financial transactions with audit reporting tools.",
      },
      {
        title: "Sku's Hub",
        icon: "inventory",
        description: "Stock management for every department with continuous number updates.",
        items: ["Academic Accessories", "Books", "Furniture", "Transport"],
      },
      {
        title: "Commutation",
        icon: "commutation",
        description:
          "Complete transportation vehicle details — total numbers, purpose of usage, working condition, and future requirements. Customizable per need.",
      },
    ],
  },
];
